/* Small shared pieces: metric tiles, badges, session rows, empty states and
 * the lap-time chart. Kept together because each is a handful of lines and a
 * file apiece would be more navigation than code. */

import { Link } from 'react-router-dom'
import clsx from 'clsx'
import type { LucideIcon } from 'lucide-react'
import type { SessionSummary } from '../lib/api'
import { lapTime, layoutName, shortDate, timeOfDay } from '../lib/format'

export function Metric({ label, value, foot, icon: Icon, tone }: {
  label: string; value: React.ReactNode; foot?: string
  icon?: LucideIcon; tone?: 'green'
}) {
  return (
    <div className="card metric">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="label">{label}</div>
        {Icon && <Icon size={17} className="icon" />}
      </div>
      <div className={clsx('value', tone === 'green' && 'lap best')}>{value}</div>
      {foot && <div className="foot">{foot}</div>}
    </div>
  )
}

export function SetupBadge({ status }: { status: SessionSummary['setup_status'] }) {
  const text = status === 'saved' ? 'Setup saved'
    : status === 'partial' ? 'Partial setup' : 'No setup'
  return <span className={clsx('badge', status)}>{text}</span>
}

export function SessionRow({ s }: { s: SessionSummary }) {
  return (
    <div className="session-row">
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{layoutName(s.track, s.layout)}</div>
        <div className="meta">
          {shortDate(s.started_at, s.timezone)}
          {' '}{timeOfDay(s.started_at, s.timezone)}
          {' · '}{s.n_laps} laps{' · '}Run {s.run_id}
        </div>
      </div>
      <div className="right">
        {s.best_lap_s ? (
          <span className="badge best">{lapTime(s.best_lap_s)}</span>
        ) : <span className="badge none">no time</span>}
        <SetupBadge status={s.setup_status} />
        <Link className="btn ghost small" to={`/sessions/${s.id}`}>View</Link>
      </div>
    </div>
  )
}

export function Empty({ title, body, action }: {
  title: string; body: string; action?: React.ReactNode
}) {
  return (
    <div className="card empty">
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  )
}

export function Spinner() { return <div className="spinner" /> }

/**
 * Best lap across recent sessions.
 *
 * Hand-drawn SVG rather than a charting library: it is one series of at most a
 * few dozen points, and the axis is inverted because a lower lap time is a
 * better result - a line that climbs when you get faster reads backwards to
 * anyone who has looked at a timing screen.
 */
export function LapChart({ points }: {
  points: { label: string; value: number; id: number }[]
}) {
  if (points.length < 2) {
    return <div className="dim small" style={{ padding: '30px 0', textAlign: 'center' }}>
      Not enough sessions yet to show a trend.
    </div>
  }

  const W = 560, H = 200, PAD_L = 46, PAD_B = 26, PAD_T = 12, PAD_R = 10
  const values = points.map((p) => p.value)
  const lo = Math.min(...values), hi = Math.max(...values)
  const span = hi - lo || 1
  const pad = span * 0.15
  const min = lo - pad, max = hi + pad

  const x = (i: number) =>
    PAD_L + (i / (points.length - 1)) * (W - PAD_L - PAD_R)
  // Inverted: faster (smaller) sits higher.
  const y = (v: number) =>
    PAD_T + ((v - min) / (max - min)) * (H - PAD_T - PAD_B)

  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.value)}`).join(' ')
  const area = `${line} L${x(points.length - 1)},${H - PAD_B} L${x(0)},${H - PAD_B} Z`
  const ticks = [min, (min + max) / 2, max]
  const best = Math.min(...values)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img"
      aria-label="Best lap time across recent sessions">
      <defs>
        <linearGradient id="fade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1684ff" stopOpacity="0.32" />
          <stop offset="100%" stopColor="#1684ff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={PAD_L} x2={W - PAD_R} y1={y(t)} y2={y(t)}
            stroke="rgba(255,255,255,.08)" />
          <text x={PAD_L - 8} y={y(t) + 4} textAnchor="end"
            fill="#667085" fontSize="10">{t.toFixed(1)}s</text>
        </g>
      ))}
      <path d={area} fill="url(#fade)" />
      <path d={line} fill="none" stroke="#1684ff" strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <circle key={p.id} cx={x(i)} cy={y(p.value)} r={p.value === best ? 4 : 3}
          fill={p.value === best ? '#37d84a' : '#3aa0ff'} />
      ))}
      {points.map((p, i) => (
        i % Math.ceil(points.length / 5) === 0 || i === points.length - 1 ? (
          <text key={`l${p.id}`} x={x(i)} y={H - 8} textAnchor="middle"
            fill="#667085" fontSize="10">{p.label}</text>
        ) : null
      ))}
    </svg>
  )
}
